/**
 * Passphrase-based encryption for the Anthropic API key.
 *
 * Uses Web Crypto: PBKDF2 (SHA-256, 250k iterations) → 256-bit AES-GCM key.
 *
 * Storage layout in localStorage under "wiseentry.encryptedKey":
 *   {
 *     v: 1,
 *     salt: <base64 16 bytes>,
 *     iv:   <base64 12 bytes>,
 *     ct:   <base64 ciphertext + 16-byte tag>
 *   }
 *
 * Threat model: a casual snooper with browser dev tools open can't read the key
 * just by looking at localStorage. A user who returns to their machine the next
 * day types the passphrase and gets the key back into memory for the session.
 *
 * This is NOT a substitute for real secret management. It's appropriate for a
 * personal-use tool with a BYO API key. The spec acknowledges this in §05.
 */

const STORAGE_KEY = 'wiseentry.encryptedKey';
const PBKDF2_ITERS = 250000;

let sessionKey = null;          // cleartext API key in memory for this session

/* -------- Public API -------- */

export function isEncryptedKeyStored() {
  return !!localStorage.getItem(STORAGE_KEY);
}

export function getSessionApiKey() {
  return sessionKey;
}

export function clearSessionApiKey() {
  sessionKey = null;
}

/** Encrypt an API key with a passphrase and persist to localStorage. */
export async function setupEncryption(apiKey, passphrase) {
  if (!apiKey) throw new Error('API key is required.');
  if (!passphrase || passphrase.length < 4) throw new Error('Passphrase must be at least 4 characters.');

  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv   = crypto.getRandomValues(new Uint8Array(12));
  const key  = await deriveKey(passphrase, salt);

  const ctBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    new TextEncoder().encode(apiKey)
  );

  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    v: 1,
    salt: b64(salt),
    iv:   b64(iv),
    ct:   b64(new Uint8Array(ctBuf))
  }));

  sessionKey = apiKey;
}

/** Try to decrypt the stored key with the passphrase. Returns the key on success. */
export async function unlockWithPassphrase(passphrase) {
  if (!passphrase) throw new Error('Passphrase is required.');
  const blob = readBlob();
  if (!blob) throw new Error('No encrypted key on this device.');

  const key = await deriveKey(passphrase, fromB64(blob.salt));

  let ptBuf;
  try {
    ptBuf = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: fromB64(blob.iv) },
      key,
      fromB64(blob.ct)
    );
  } catch {
    throw new Error('Incorrect passphrase.');
  }

  const apiKey = new TextDecoder().decode(ptBuf);
  sessionKey = apiKey;
  return apiKey;
}

/** Change the passphrase. Requires the current passphrase to unlock first. */
export async function changePassphrase(currentPass, newPass) {
  if (!sessionKey) {
    // Need to re-derive from current pass
    await unlockWithPassphrase(currentPass);
  } else {
    // Verify the current passphrase
    try {
      await unlockWithPassphrase(currentPass);
    } catch {
      throw new Error('Current passphrase is wrong.');
    }
  }
  if (!newPass || newPass.length < 4) throw new Error('New passphrase must be at least 4 characters.');

  await setupEncryption(sessionKey, newPass);
}

/** Forget the encrypted key on this device entirely. */
export function forgetEncryptedKey() {
  localStorage.removeItem(STORAGE_KEY);
  sessionKey = null;
}

/* -------- Internals -------- */

async function deriveKey(passphrase, salt) {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(passphrase),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt,
      iterations: PBKDF2_ITERS,
      hash: 'SHA-256'
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

function readBlob() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw); }
  catch { return null; }
}

function b64(bytes) {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function fromB64(str) {
  const s = atob(str);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}
